// Interpreten (Artists, Spotify-Vorbild) — CMS-Verwaltung. Liste + Create/Edit-Form (showForm-Muster
// wie AlbumManagement). Owner = EE-System; Authz macht das Backend. Plan: docs/plans/plan-artist-entity-2026-08.md.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PlusIcon, PhotoIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { artistService } from '../services/artistService';
import { ArtistForm } from '../components/forms/ArtistForm';
import { PageLayout } from '../components/ui/PageLayout';
import { Artist, ArtistStatus } from '../types/artist';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { Filterleiste } from '../components/ui/Filterleiste';
import { SeitenblaetterFuss } from '../components/ui/SeitenblaetterFuss';
import { kuratierStatusAuswahl, seitenwechsel } from '../components/ui/filterZustand';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { useSeitenausschnitt } from '../hooks/useSeitenausschnitt';

/** Zeilen je Seite — dieselbe Begründung wie in `AlbumManagement`: reine Anzeige-Größe. */
const ZEILEN_JE_SEITE = 20;

/** Filter-Zustand dieser Seite; `page` liegt aus demselben Grund mit drin wie bei den Alben. */
interface ArtistFilter {
  page: number;
  suche: string;
  status: ArtistStatus | undefined;
}

const ArtistManagement: React.FC = () => {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Artist | null>(null);
  const [filter, setFilter] = useState<ArtistFilter>({ page: 1, suche: '', status: undefined });
  // `melde` trägt exakt die Signatur, die `ArtistForm` als `notify` erwartet — das Formular bleibt
  // deshalb unangetastet. Der frühere Handbau setzte seinen `setTimeout` ohne `clearTimeout` und
  // räumte auch Fehler nach vier Sekunden weg; beides liegt jetzt im Haken.
  const { inhalt, melde, schließe } = useBenachrichtigung();

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setArtists(await artistService.getArtists());
    } catch {
      melde('error', 'Interpreten konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [melde]);

  useEffect(() => {
    void load();
  }, [load]);

  /** `page: 1` steht HINTER dem gesetzten Wert — Begründung bei `seitenwechsel`. */
  const setzeFilter = <K extends keyof ArtistFilter>(feld: K, wert: ArtistFilter[K]) => {
    setFilter((vorher) => ({ ...vorher, [feld]: wert, page: 1 }));
  };

  /**
   * Gesucht wird über den Namen — die einzige Textspalte dieser Tabelle.
   *
   * ⚠️ Die Kurzbiografie (`bio`) ist bewusst NICHT mitdurchsucht, obwohl sie am Datensatz hängt:
   * ein Treffer darin wäre in der Liste unsichtbar. Die Nutzerin sähe eine Zeile, deren Name den
   * Suchbegriff nicht enthält, und hielte die Suche für kaputt.
   *
   * ⚠️ Keine Entprellung — es geht keine Anfrage raus (siehe `useSeitenausschnitt`).
   */
  const gefiltert = useMemo(() => {
    const suche = filter.suche.trim().toLowerCase();
    return artists.filter((ar) => {
      if (filter.status && ar.status !== filter.status) return false;
      if (!suche) return true;
      return ar.name.toLowerCase().includes(suche);
    });
  }, [artists, filter.suche, filter.status]);

  const { ausschnitt, seite, seitenAnzahl, gesamt } = useSeitenausschnitt(
    gefiltert,
    ZEILEN_JE_SEITE,
    filter,
    setFilter,
  );

  const handleCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = async (ar: Artist) => {
    try {
      const full = await artistService.getArtistById(ar.id);
      setEditing(full);
    } catch {
      setEditing(ar);
    }
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditing(null);
    void load();
  };

  const handleDeleted = () => {
    setShowForm(false);
    setEditing(null);
    melde('success', 'Interpret gelöscht');
    void load();
  };

  if (showForm) {
    return (
      <PageLayout title={editing ? 'Interpret bearbeiten' : 'Neuer Interpret'}>
        {/* Zweiter Aufrufort desselben Bausteins: der Formular-Zweig. Vorher standen hier und im
            Listen-Zweig zwei handgleiche Kopien — wer eine anfasste, sah die andere nicht. */}
        <Benachrichtigung inhalt={inhalt} onSchließen={schließe} />
        <ArtistForm
          key={editing?.id || 'new-artist'}
          initial={editing}
          onCreated={(created) => setEditing(created)}
          onClose={handleClose}
          onDeleted={handleDeleted}
          notify={melde}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Interpreten"
      subtitle="Wählen Sie den Interpreten pro Titel im Audio-Formular."
      actions={
        <button onClick={handleCreate} className="btn btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />Neuer Interpret
        </button>
      }
    >
      <Benachrichtigung inhalt={inhalt} onSchließen={schließe} />

      {/* An `artists.length`, nicht an `gefiltert.length` — sonst nähme der letzte Treffer das
          Suchfeld mit, in dem der zu enge Begriff steht (Herleitung in `AlbumManagement`). */}
      {artists.length > 0 && (
        <Filterleiste
          suche={filter.suche}
          onSuche={(wert) => setzeFilter('suche', wert)}
          platzhalter="Name..."
          auswahlen={[
            kuratierStatusAuswahl(filter.status, (wert) => setzeFilter('status', wert)),
          ]}
        />
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto w-8 h-8"></div>
          <p className="mt-4 text-secondary-600">Lade Interpreten…</p>
        </div>
      ) : artists.length === 0 ? (
        <div className="text-center py-12 card">
          <UserGroupIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Noch keine Interpreten</p>
          <button onClick={handleCreate} className="mt-4 btn btn-primary">
            <PlusIcon className="w-5 h-5 mr-2" />Ersten Interpreten erstellen
          </button>
        </div>
      ) : gefiltert.length === 0 ? (
        /* Zweiter leerer Zustand: „nichts gefunden" ist nicht „nichts vorhanden" — deshalb ohne
           den Anlegen-Knopf des Zweigs darüber. */
        <div className="text-center py-12 card">
          <UserGroupIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Keine Interpreten gefunden</p>
          <p className="mt-1 text-sm text-secondary-500">Suche oder Status anpassen.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {/* Kopf- und Zellenmaße kommen aus `.table` (index.css) statt aus handkopierten
              Klassenketten. Was dabei bewusst aufgegeben wurde, steht dort am Regelblock. */}
          <table className="table">
            <thead>
              <tr>
                <th>Bild</th>
                <th>Name</th>
                <th>Titel</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ausschnitt.map((ar) => (
                <tr key={ar.id}>
                  <td>
                    <button type="button" onClick={() => void handleEdit(ar)} title="Bearbeiten" className="focus:outline-none">
                      {ar.profileImageUrl ? (
                        <img src={ar.profileImageUrl} alt={ar.name} className="h-10 w-10 rounded-full object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-secondary-100 flex items-center justify-center"><PhotoIcon className="w-5 h-5 text-secondary-400" /></div>
                      )}
                    </button>
                  </td>
                  <td>
                    <button type="button" onClick={() => void handleEdit(ar)} className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline text-left focus:outline-none">
                      {ar.name}
                    </button>
                  </td>
                  <td>{ar.itemCount}</td>
                  <td><StatusBadge status={ar.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* `gesamt` zählt die Treffer, nicht alle Interpreten — sonst widerspräche die Zahl der
              Tabelle darüber, sobald ein Filter steht. */}
          <SeitenblaetterFuss
            seite={seite}
            seitenAnzahl={seitenAnzahl}
            gesamt={gesamt}
            onSeitenwechsel={seitenwechsel(setFilter)}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default ArtistManagement;
